import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Zap, Moon, Sun, Globe, ShieldCheck, Mail, ArrowLeft } from 'lucide-react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// --- SHARED UI ELEMENTS ---

const LegalNavbar = ({ isDarkMode, setIsDarkMode }: any) => {
  const location = useLocation();


  return (
    <nav className={cn(
      "fixed top-0 w-full z-50 backdrop-blur-md border-b flex items-center h-16 transition-colors",
      isDarkMode ? "bg-black/80 border-zinc-800" : "bg-white/80 border-zinc-200"
    )}>
      <div className="flex justify-between items-center px-6 max-w-6xl mx-auto w-full">
        <div className="flex items-center gap-8">
          <Link to="/" className="flex items-center gap-2 font-black text-xl tracking-tighter italic">
            <Zap className="text-purple-500 fill-current" size={22} />
            <span className={isDarkMode ? "text-white" : "text-zinc-900"}>THRUST</span>
          </Link>
          <div className="hidden md:flex gap-6 text-[10px] font-black uppercase tracking-[0.2em] opacity-50">
            <Link to="/terms" className={cn("hover:opacity-100", location.pathname === '/terms' && "text-purple-500 opacity-100")}>Terms</Link>
            <Link to="/privacy" className={cn("hover:opacity-100", location.pathname === '/privacy' && "text-purple-500 opacity-100")}>Privacy</Link>
          </div>
        </div>
        <button onClick={() => setIsDarkMode(!isDarkMode)} className="p-2 rounded-lg hover:bg-zinc-500/10 transition-colors">
          {isDarkMode ? <Sun size={18} className="text-zinc-400" /> : <Moon size={18} className="text-zinc-500" />}
        </button>
      </div>
    </nav>
  );
};

const LegalFooter = ({ isDarkMode }: any) => (
  <footer className={cn(
    "py-20 px-6 border-t mt-20 transition-colors",
    isDarkMode ? "bg-black border-zinc-900" : "bg-zinc-50 border-zinc-200"
  )}>
    <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
      <div className="space-y-4">
        <div className="flex items-center gap-2 font-black text-xl tracking-tighter italic opacity-50">
          <Zap className="text-purple-500 fill-current" size={20} /> THRUST
        </div>
        <p className="text-xs font-medium opacity-40 max-w-xs">
          High-velocity project intelligence for the modern technical professional. 
          Oslo, Norway.
        </p>
      </div>
      <div className="flex justify-center md:justify-end gap-8 opacity-30">
        <Globe size={18} />
        <ShieldCheck size={18} />
        <Mail size={18} />
      </div>
    </div>
  </footer>
);

// --- TERMS OF SERVICE ---

export const Terms = () => {
  const [isDarkMode, setIsDarkMode] = useState(true);
const LastUpdated = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div className={cn("min-h-screen transition-colors duration-500", isDarkMode ? "bg-black text-zinc-400" : "bg-white text-zinc-600")}>
      <LegalNavbar isDarkMode={isDarkMode} setIsDarkMode={setIsDarkMode} />
      
      <main className="max-w-4xl mx-auto pt-40 px-6">
        <header className="mb-20">
          <h1 className={cn("text-5xl md:text-7xl font-black tracking-tighter mb-6", isDarkMode ? "text-white" : "text-zinc-900")}>
            Terms of <span className="text-purple-500">Service.</span>
          </h1>
          <p className="text-sm font-bold uppercase tracking-widest opacity-50">
	  Effective Date: {LastUpdated}</p>
        </header>

        <div className="space-y-16 pb-20 border-l border-zinc-200 dark:border-zinc-800 pl-8 md:pl-16 relative">
          <div className="absolute top-0 left-[-1px] w-[1px] h-20 bg-gradient-to-b from-purple-500 to-transparent" />
          
          <section>
            <h2 className={cn("text-2xl font-bold mb-6 tracking-tight", isDarkMode ? "text-zinc-100" : "text-zinc-900")}>1. Agreement</h2>
            <p className="leading-relaxed mb-4">This Software as a Service Agreement (the "Agreement") is a legal contract between you ("User", "Licensee") and Thrust Inc. ("Company", "We", "Us"). By creating an account or accessing the Thrust platform, you acknowledge that you have read, understood, and agree to be bound by the terms herein.</p>
          </section>

          <section>
            <h2 className={cn("text-2xl font-bold mb-6 tracking-tight", isDarkMode ? "text-zinc-100" : "text-zinc-900")}>2. License & Access</h2>
            <p className="leading-relaxed mb-4">We grant you a non-exclusive, non-transferable, revocable license to use Thrust strictly in accordance with these terms. We reserve the right to terminate access for any conduct that we, in our sole discretion, believe violates any applicable law or is harmful to our interests.</p>
          </section>

          <section>
            <h2 className={cn("text-2xl font-bold mb-6 tracking-tight", isDarkMode ? "text-zinc-100" : "text-zinc-900")}>3. Intellectual Property</h2>
            <div className="space-y-4">
              <p><strong>3.1 User Content:</strong> Users retain all ownership and intellectual property rights to the code, designs, and project data analyzed by the service. Thrust does not claim any rights to your proprietary work.</p>
              <p><strong>3.2 Service Rights:</strong> All rights, title, and interest in and to the Service (excluding User Content) are and will remain the exclusive property of Thrust Inc. and its licensors.</p>
            </div>
          </section>

          <section>
            <h2 className={cn("text-2xl font-bold mb-6 tracking-tight", isDarkMode ? "text-zinc-100" : "text-zinc-900")}>4. Paddle & Merchant of Record</h2>
            <p className="leading-relaxed">Our order process is conducted by our online reseller Paddle.com. Paddle.com is the Merchant of Record for all our orders. Paddle provides all customer service inquiries and handles returns.</p>
          </section>

          <section>
            <h2 className={cn("text-2xl font-bold mb-6 tracking-tight", isDarkMode ? "text-zinc-100" : "text-zinc-900")}>5. Refunds & Cancellations</h2>
            <p className="leading-relaxed mb-4">You may cancel your subscription at any time. In accordance with Paddle's merchant requirements, a minimum 14-day refund period is a general requirement for all our users. If you are dissatisfied with the Service for any reason, you are entitled to a full refund within 14 days of your initial transaction.</p>
          </section>

          <section>
            <h2 className={cn("text-2xl font-bold mb-6 tracking-tight", isDarkMode ? "text-zinc-100" : "text-zinc-900")}>6. Limitation of Liability</h2>
            <p className="leading-relaxed">Thrust is provided "as is". To the maximum extent permitted by law, Thrust Inc. shall not be liable for any indirect, incidental, special, consequential or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data, use, or other intangible losses resulting from (i) your access to or use of or inability to access or use the services; (ii) any conduct or content of any third party on the services.</p>
          </section>

          <section>
            <h2 className={cn("text-2xl font-bold mb-6 tracking-tight", isDarkMode ? "text-zinc-100" : "text-zinc-900")}>7. Governing Law</h2>
            <p className="leading-relaxed">These Terms shall be governed by the laws of Norway. Any disputes arising out of or in connection with these Terms shall be settled by the Oslo District Court.</p>
          </section>
        </div>
      </main>
      <LegalFooter isDarkMode={isDarkMode} />
    </div>
  );
};

// --- PRIVACY POLICY ---

export const Policy = () => {
  const [isDarkMode, setIsDarkMode] = useState(true);
const LastUpdated = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div className={cn("min-h-screen transition-colors duration-500", isDarkMode ? "bg-black text-zinc-400" : "bg-white text-zinc-600")}>
      <LegalNavbar isDarkMode={isDarkMode} setIsDarkMode={setIsDarkMode} />
      
      <main className="max-w-4xl mx-auto pt-40 px-6">
        <header className="mb-20">
          <h1 className={cn("text-5xl md:text-7xl font-black tracking-tighter mb-6", isDarkMode ? "text-white" : "text-zinc-900")}>
            Privacy <span className="text-purple-500">Policy.</span>
          </h1>
          <p className="text-sm font-bold uppercase tracking-widest opacity-50">
	  Last Modified: {LastUpdated}</p>  </header>

        <div className="space-y-16 pb-20 border-l border-zinc-200 dark:border-zinc-800 pl-8 md:pl-16 relative">
          <div className="absolute top-0 left-[-1px] w-[1px] h-20 bg-gradient-to-b from-purple-500 to-transparent" />

          <section>
            <h2 className={cn("text-2xl font-bold mb-6 tracking-tight", isDarkMode ? "text-zinc-100" : "text-zinc-900")}>1. Information Collection</h2>
            <p className="leading-relaxed">We collect information to provide a better experience to our users. This includes account information (email), technical metadata (system logs), and project metadata (hashes of project files). We do not collect or store your actual source code on our permanent databases unless explicitly required for a specific AI-feature handshake.</p>
          </section>

          <section>
            <h2 className={cn("text-2xl font-bold mb-6 tracking-tight", isDarkMode ? "text-zinc-100" : "text-zinc-900")}>2. Data Usage & AI</h2>
            <p className="leading-relaxed mb-4">Your project data is used strictly to provide the "Proactive Thrust" briefings. Thrust does not train its core AI models on User Content. Data processed via our LLM providers (e.g., Anthropic, OpenAI) is governed by Enterprise Zero-Data Retention policies where applicable.</p>
          </section>

          <section>
            <h2 className={cn("text-2xl font-bold mb-6 tracking-tight", isDarkMode ? "text-zinc-100" : "text-zinc-900")}>3. Paddle & Payments</h2>
            <p className="leading-relaxed">Personal data required for payments is collected and processed by Paddle.com. We do not store credit card information on our servers. Please refer to Paddle’s privacy policy for details on how they secure your transaction data.</p>
          </section>

          <section>
            <h2 className={cn("text-2xl font-bold mb-6 tracking-tight", isDarkMode ? "text-zinc-100" : "text-zinc-900")}>4. Your Rights (GDPR)</h2>
            <p className="leading-relaxed mb-4">Under the GDPR, users in the EEA have the following rights: Access to data, Rectification of errors, Erasure (Right to be Forgotten), and Portability. To exercise these rights, please contact legal@thrust.dev.</p>
          </section>

          <section>
            <h2 className={cn("text-2xl font-bold mb-6 tracking-tight", isDarkMode ? "text-zinc-100" : "text-zinc-900")}>5. Security</h2>
            <p className="leading-relaxed">We implement industry-standard security measures including SSL encryption and isolated project environments to protect your data. However, no method of transmission over the Internet is 100% secure.</p>
          </section>
        </div>
      </main>
      <LegalFooter isDarkMode={isDarkMode} />
    </div>
  );
};
